import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import { RocketLaunch as BoostIcon, Refresh as RefreshIcon, Cancel as CancelIcon, Edit as EditIcon, DoNotDisturb as RejectIcon } from '@mui/icons-material';
import ApiService from '../../services/api';
import { isBoostActive } from '../../utils/boost';

// Admin page to manage job boosts: Pending, Approved, Rejected
const JobBoosts = () => {
  const [tab, setTab] = useState(0); // 0: Pending(requested), 1: Approved(approved), 2: Rejected(rejected)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expiresMap, setExpiresMap] = useState({});

  const statusForTab = useMemo(() => (tab === 0 ? 'requested' : tab === 1 ? 'approved' : 'rejected'), [tab]);

  const loadJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all jobs including non-public statuses for admin view
      const resp = await ApiService.getJobs({ includeAll: 'true', limit: 1000 });
      const jobs = resp?.jobs || resp?.data?.jobs || resp || [];

      // Filter by boost status per tab
      const filterByTab = (j) => {
        if (statusForTab === 'requested') return j.boostStatus === 'requested';
        if (statusForTab === 'approved') return (j.boostStatus === 'approved') || isBoostActive(j.boosted, j.boostExpiresAt);
        if (statusForTab === 'rejected') return j.boostStatus === 'rejected';
        return false;
      };

      const filtered = (Array.isArray(jobs) ? jobs : []).filter(filterByTab);
      const sorted = filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setRows(sorted);
    } catch (err) {
      console.error('Failed to load jobs for boosts', err);
      setError(err?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusForTab]);

  const handleApprove = async (job) => {
    try {
      const explicitExpiry = expiresMap[job._id];
      const payload = {};
      if (explicitExpiry) {
        payload.expiresAt = explicitExpiry;
      } else {
        // Default to 30 days
        payload.durationDays = 30;
      }
      await ApiService.boostJob(job._id, payload);
      setSnackbar({ open: true, message: 'Job boost approved', severity: 'success' });
      loadJobs();
    } catch (err) {
      console.error('Failed to approve job boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to approve job boost', severity: 'error' });
    }
  };

  const handleCancel = async (job) => {
    try {
      await ApiService.cancelJobBoost(job._id);
      setSnackbar({ open: true, message: 'Job boost canceled', severity: 'success' });
      loadJobs();
    } catch (err) {
      console.error('Failed to cancel job boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to cancel job boost', severity: 'error' });
    }
  };

  const handleEdit = async (job) => {
    try {
      const expiresAt = expiresMap[job._id];
      if (!expiresAt) throw new Error('Please set new expiry date');
      await ApiService.editJobBoost(job._id, { expiresAt });
      setSnackbar({ open: true, message: 'Job boost updated', severity: 'success' });
      loadJobs();
    } catch (err) {
      console.error('Failed to edit job boost', err);
      setSnackbar({ open: true, message: err?.message || 'Failed to edit job boost', severity: 'error' });
    }
  };

  const handleReject = async (job) => {
    try {
      // For now, rejection is handled by setting boostStatus via dedicated admin route if available.
      // If not present, show a helpful error.
      setSnackbar({ open: true, message: 'Use review to reject boost requests', severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || 'Failed to reject job boost', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Job Boosts</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadJobs} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Pending" />
        <Tab label="Approved" />
        <Tab label="Rejected" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Employer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Boost Expires</TableCell>
              <TableCell>Set Expiry</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No jobs found for this tab.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((j) => (
                <TableRow key={j._id} hover>
                  <TableCell>{j.title}</TableCell>
                  <TableCell>{j.companyName || j.employerName || j.employer?.email || '-'}</TableCell>
                  <TableCell>
                    <Chip label={j.boostStatus || (j.boosted ? 'approved' : 'none')} color={(j.boostStatus === 'requested') ? 'warning' : (j.boostStatus === 'approved' || j.boosted ? 'success' : (j.boostStatus === 'rejected' ? 'error' : 'default'))} size="small" />
                  </TableCell>
                  <TableCell>{j.boostExpiresAt ? new Date(j.boostExpiresAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={(expiresMap[j._id] || '').slice(0, 10)}
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value).toISOString() : '';
                        setExpiresMap(prev => ({ ...prev, [j._id]: date }));
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {statusForTab === 'requested' && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="secondary" size="small" startIcon={<BoostIcon />} onClick={() => handleApprove(j)}>Approve</Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<RejectIcon />} onClick={() => handleReject(j)}>Reject</Button>
                      </Box>
                    )}
                    {statusForTab === 'approved' && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" color="error" size="small" startIcon={<CancelIcon />} onClick={() => handleCancel(j)}>Cancel</Button>
                        <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => handleEdit(j)}>Edit</Button>
                      </Box>
                    )}
                    {statusForTab === 'rejected' && (
                      <Typography variant="body2" color="text.secondary" align="right">No actions</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobBoosts;